import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

import { useGeneratePromo, useSavePromo } from '../../../../queries/promoDesigner';
import { useCustomToast } from '../../../Contexts/ToastContext';

/**
 * AI Promo Designer dialog — type a one-line promotion (Arabic or English) +
 * pick a style + aspect → Nano Banana returns finished promo POSTER options →
 * pick one → saved to the media library + Promotions gallery.
 *
 * Hybrid text is server-side: Latin promos are baked by the model, Arabic promos
 * get a text-free AI background + a programmatic Arabic overlay. The client just
 * shows the finished posters. On save, calls onSaved(promo) and closes.
 */
const STYLES = ['bold_vibrant', 'elegant_premium', 'fun_playful', 'minimal_clean', 'festive', 'appetizing_photo'];
const ASPECTS = [
  { key: '16:9', icon: 'pi pi-window-maximize', ratio: '16 / 9' },
  { key: '9:16', icon: 'pi pi-mobile', ratio: '9 / 16' },
  { key: '1:1', icon: 'pi pi-stop', ratio: '1 / 1' },
];
const dataUrl = (img) => `data:${img.mime};base64,${img.b64}`;

export default function PromoDesignerDialog({ visible, onHide, onSaved }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const gen = useGeneratePromo();
  const save = useSavePromo();

  const [text, setText] = useState('');
  const [style, setStyle] = useState('bold_vibrant');
  const [aspect, setAspect] = useState('16:9');
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null); // token
  const [zoom, setZoom] = useState(null); // data URL of the option being previewed full-size
  const [error, setError] = useState(null);

  const ratio = ASPECTS.find((a) => a.key === aspect)?.ratio || '16 / 9';
  const busy = gen.isLoading || save.isLoading;

  useEffect(() => { if (!visible) reset(); /* eslint-disable-next-line */ }, [visible]);

  const reset = () => { setText(''); setOptions([]); setPicked(null); setError(null); };
  const close = () => { onHide(); };

  const doGenerate = async () => {
    if (!text.trim()) return;
    setError(null); setOptions([]); setPicked(null);
    try {
      const res = await gen.mutateAsync({ text: text.trim(), style, aspect, count: 3 });
      if (res?.status === 422 || !res?.options?.length) {
        setError(res?.msg || t('video_wall.promo_ai.gen_failed'));
        return;
      }
      setOptions(res.options);
    } catch { setError(t('video_wall.promo_ai.gen_failed')); }
  };

  const persist = async () => {
    if (!picked) return;
    try {
      const promo = await save.mutateAsync({ token: picked, name: text.trim().slice(0, 80) || undefined });
      if (!promo?.media_id) { setError(t('video_wall.promo_ai.save_failed')); return; }
      showToast('success', t('video_wall.promo_ai.title'), t('video_wall.promo_ai.saved_toast'));
      onSaved?.(promo);
      close();
    } catch { setError(t('video_wall.promo_ai.save_failed')); }
  };

  return (
    <Dialog visible={visible} onHide={close} style={{ width: 'min(96vw, 720px)' }}
      header={(
        <span><i className="pi pi-sparkles me-2" style={{ color: 'var(--pp-primary)' }} />
          {t('video_wall.promo_ai.title')}</span>
      )}>
      {error && <Message severity="error" text={error} className="w-100 mb-3" />}

      <div className="d-flex flex-column gap-3">
        {/* promo text */}
        <div>
          <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.text_label')}</label>
          <InputTextarea className="w-100" rows={2} autoResize value={text} maxLength={300} disabled={busy}
            dir="auto" onChange={(e) => setText(e.target.value)}
            placeholder={t('video_wall.promo_ai.text_ph')} />
        </div>

        {/* style chips */}
        <div>
          <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.style')}</label>
          <div className="d-flex flex-wrap gap-2">
            {STYLES.map((s) => (
              <Button key={s} size="small" label={t(`video_wall.promo_ai.style_${s}`)}
                outlined={style !== s} onClick={() => setStyle(s)} disabled={busy} />
            ))}
          </div>
        </div>

        {/* aspect ratio */}
        <div>
          <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.aspect')}</label>
          <div className="d-flex gap-2">
            {ASPECTS.map((a) => (
              <Button key={a.key} size="small" icon={a.icon} label={t(`video_wall.promo_ai.aspect_${a.key.replace(':', '_')}`)}
                outlined={aspect !== a.key} onClick={() => setAspect(a.key)} disabled={busy} />
            ))}
          </div>
        </div>

        {/* results */}
        {gen.isLoading ? (
          <div className="text-center py-5">
            <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
            <h6 className="mt-3 mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.promo_ai.generating')}</h6>
            <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.generating_hint')}</p>
          </div>
        ) : options.length ? (
          <>
            <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.pick_hint')}</p>
            <div className="row g-2">
              {options.map((o) => (
                <div key={o.token} className="col-4" style={{ position: 'relative' }}>
                  <button type="button" className="p-0 w-100" style={{ background: 'transparent', cursor: 'pointer',
                    border: `3px solid ${picked === o.token ? 'var(--pp-btn-honey)' : 'transparent'}`, borderRadius: 12 }}
                    onClick={() => setPicked(o.token)}>
                    <img src={dataUrl(o)} alt="" className="w-100" style={{ aspectRatio: ratio, objectFit: 'cover', borderRadius: 9 }} />
                  </button>
                  <Button type="button" icon="pi pi-search-plus" rounded aria-label={t('video_wall.promo_ai.zoom')}
                    onClick={(e) => { e.stopPropagation(); setZoom(dataUrl(o)); }}
                    style={{ position: 'absolute', top: 8, insetInlineEnd: 8, width: 30, height: 30,
                      background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff' }} />
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-between gap-2">
              <Button text icon="pi pi-refresh" label={t('video_wall.promo_ai.regenerate')} onClick={doGenerate} disabled={busy} />
              <Button icon="pi pi-check" label={t('video_wall.promo_ai.use_promo')}
                disabled={!picked || busy} loading={save.isLoading} onClick={persist} />
            </div>
          </>
        ) : (
          <Button icon="pi pi-sparkles" label={t('video_wall.promo_ai.generate')}
            disabled={!text.trim() || busy} onClick={doGenerate} />
        )}
      </div>

      {/* Full-size preview (lightbox) of a generated option */}
      <Dialog visible={!!zoom} onHide={() => setZoom(null)} dismissableMask draggable={false}
        style={{ width: 'min(96vw, 1000px)' }} header={t('video_wall.promo_ai.preview')}>
        {zoom && <img src={zoom} alt="" style={{ width: '100%', height: 'auto', borderRadius: 8, display: 'block' }} />}
      </Dialog>
    </Dialog>
  );
}
